/* eslint-disable react-refresh/only-export-components, @typescript-eslint/no-unused-expressions, react-hooks/exhaustive-deps */
// @ts-nocheck
import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useCallback,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useRecoilValue, useRecoilState } from 'recoil';
import type { Dayjs } from 'dayjs';

import {
  selectedHiroTabsState,
  userDetailsState,
  hiroETHState,
  lenseState,
  optionTypeState,
  candleDurationState,
  hiroLineTypeState,
  priceLineTypeState,
  hiroViewAlertsState,
  sumWindowState,
  hiroShowKeyLevels,
  bottomCandlesTypeState,
  hiroIndicatorsState,
  hiroTotalsEnabledPutsCallsDisabledState,
  hiroAllTradesEnabledState,
  hiroNextExpiryEnabledState,
  hiroEnableCandlesState,
  isInstitutionalLocalState,
  negativeTrendColorState,
  positiveTrendColorState,
  userDashAccessState,
  newTVHiroState,
  startQueryDateState,
  endQueryDateState,
  showChartTooltipState,
} from 'states';

import { hasDashAccess, DashAccess } from 'types/user';
import { getEnv } from 'util/shared/common';
import { InitialHiroData } from 'util/hiro';
import { isBloomberg } from 'util/bloomberg';
import type {
  BottomCandlesType,
  HiroIndicator,
  HiroLense,
  HiroLineType,
  HiroTimeseries,
  RawSpotgammaLevels,
  UserDetails,
} from 'types';
import { ProductType, HiroUpdatingType, SymbolLineParams } from 'types';
import useSetSym from 'hooks/hiro/useSetSym';
import useUserDetails from 'hooks/user/useUserDetails';

// settings that are common across all HIRO instances
interface HiroGlobalPersistentSettings {
  enableHiroV2: boolean;
  enableHiroV3: boolean;

  // SymbolTabs
  hiroTabs: string[];

  userDetails?: UserDetails;

  // HiroChart (lightweight-charts / trading_view)
  isInstitutionalLocal: boolean | null;
}

export interface HiroPerInstPersistentSettings
  extends HiroGlobalPersistentSettings {
  sym: string;

  // Chart display state
  includeHiroETH: boolean;
  selectedLenses: { [value in HiroLense]: number };
  optionType: 'total' | 'pc';
  candleDuration: number;
  hiroUpdatingType: HiroUpdatingType;
  hiroLineType: HiroLineType;
  priceLineType: HiroLineType;
  showAlerts: boolean;
  sumWindow: number;
  showHiroKeyLevels: boolean;

  bottomCandlesType: BottomCandlesType;

  // Indicators
  hiroIndicators: HiroIndicator[];
  hiroTotalsEnabledPutsCallsDisabled: boolean;
  hiroAllTradesEnabled: boolean;
  hiroNextExpiryEnabled: boolean;
  hiroRollingWindowSeconds: number;
  hiroEnableCandles: boolean;

  negativeTrendColor: string;
  positiveTrendColor: string;
}

export interface HiroContextValue extends HiroPerInstPersistentSettings {
  // non-persistent settings

  showChartTooltip: boolean;
  hiroSelectedSeriesList: {
    seriesName: string;
    symbolLineParams: SymbolLineParams;
  }[];

  // Dates
  endQueryDate: Dayjs;
  startQueryDate: Dayjs;
  lastSetDate?: Dayjs;

  // HiroTradingViewContainer / useHiroData
  hiroData: HiroTimeseries;
  sgData?: RawSpotgammaLevels;

  zoomTime?: Dayjs;

  bottomDrawerExpanded: boolean;

  update: (u: Partial<HiroContextValue>) => void;
  mode: 'standalone' | 'canvas';
  canvasId?: number;
  expandableContentButtonType?: string;
}

export const HiroContext = createContext<HiroContextValue | null>(null);

export const useHiroCtx = (): HiroContextValue => {
  const ctx = useContext(HiroContext);
  if (ctx == null) {
    throw new Error('useHiroCtx must be used within a HiroProvider');
  }
  return ctx;
};

interface HiroProviderProps {
  mode?: 'standalone' | 'canvas';

  config?: Partial<HiroPerInstPersistentSettings>;
  onConfigChange?: (patch: Partial<HiroPerInstPersistentSettings>) => void;

  canvasId?: number;
  expandableContentButtonType?: string;

  children: ReactNode;
}

export const HiroProvider = ({
  mode = 'standalone',
  config,
  onConfigChange,
  children,
  canvasId,
  expandableContentButtonType,
}: HiroProviderProps) => {
  const { changeHiroChartSetting } = useUserDetails();

  ///////////////
  // global state
  ///////////////
  const [enableHiroV2, setEnableHiroV2] = useRecoilState(newTVHiroState);

  const userDashAccess = useRecoilValue(userDashAccessState);
  const enableHiroV3 =
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    !localStorage.enableHiroV2 &&
    (import.meta.env.DEV ||
      (hasDashAccess(userDashAccess, DashAccess.BETA) &&
        getEnv() !== 'production'));

  // SymbolTabs
  const [hiroTabs, setHiroTabs] = useRecoilState(selectedHiroTabsState);

  const userDetails = useRecoilValue(userDetailsState);

  const [isInstitutionalLocal, setInstitutionalLocal] = useRecoilState(
    isInstitutionalLocalState,
  );

  /////////////////
  // per inst state
  /////////////////

  const { getSym, setSym } = useSetSym();
  const [searchParams] = useSearchParams();
  const sym = searchParams.get('sym') ?? getSym(ProductType.HIRO);

  // Chart display state
  const includeHiroETH = useRecoilValue(hiroETHState);
  const setIncludeHiroETH = useCallback(
    (v: boolean) => changeHiroChartSetting({ showExtendedHours: v }),
    [],
  );

  const selectedLenses = useRecoilValue(lenseState);
  const setSelectedLenses = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
    (lense: any) => changeHiroChartSetting({ selectedLenses: lense }),
    [],
  );
  const optionType = useRecoilValue(optionTypeState);
  const setOptionType = useCallback(
    (v: 'total' | 'pc') => changeHiroChartSetting({ optionType: v }),
    [],
  );
  const candleDuration = useRecoilValue(candleDurationState);
  const setCandleDuration = useCallback(
    (v: number) =>
      changeHiroChartSetting({
        candleDurationSecs: v,
      }),
    [],
  );
  const hiroUpdatingType = isBloomberg()
    ? HiroUpdatingType.STREAMING
    : HiroUpdatingType.POLLING;

  const hiroLineType = useRecoilValue(hiroLineTypeState);
  const setHiroLineType = useCallback(
    (v: HiroLineType) => changeHiroChartSetting({ hiroLineType: v }),
    [],
  );
  const priceLineType = useRecoilValue(priceLineTypeState);
  const setPriceLineType = useCallback(
    (v: HiroLineType) => changeHiroChartSetting({ priceLineType: v }),
    [],
  );
  const showAlerts = useRecoilValue(hiroViewAlertsState);
  const setShowAlerts = useCallback(
    (v: boolean) => changeHiroChartSetting({ showAlerts: v }),
    [],
  );
  const sumWindow = useRecoilValue(sumWindowState);
  const setSumWindow = useCallback(
    (v: number) => changeHiroChartSetting({ sumWindow: v }),
    [],
  );
  const showHiroKeyLevels = useRecoilValue(hiroShowKeyLevels);
  const setShowHiroKeyLevels = useCallback(
    (v: boolean) => changeHiroChartSetting({ showKeyLevels: v }),
    [],
  );

  const bottomCandlesType = useRecoilValue(bottomCandlesTypeState);
  const setBottomCandlesType = useCallback(
    (v: BottomCandlesType) => changeHiroChartSetting({ bottomCandlesType: v }),
    [],
  );

  // Indicators
  const [hiroIndicators, setHiroIndicators] =
    useRecoilState(hiroIndicatorsState);

  const [
    hiroTotalsEnabledPutsCallsDisabled,
    setHiroTotalsEnabledPutsCallsDisabled,
  ] = useRecoilState(hiroTotalsEnabledPutsCallsDisabledState);
  const [hiroAllTradesEnabled, setHiroAllTradesEnabled] = useRecoilState(
    hiroAllTradesEnabledState,
  );
  const [hiroNextExpiryEnabled, setHiroNextExpiryEnabled] = useRecoilState(
    hiroNextExpiryEnabledState,
  );
  const [hiroEnableCandles, setHiroEnableCandles] = useRecoilState(
    hiroEnableCandlesState,
  );

  // HiroChart (lightweight-charts / trading_view)
  const negativeTrendColor = useRecoilValue(negativeTrendColorState);
  const positiveTrendColor = useRecoilValue(positiveTrendColorState);

  //////////////////
  // ephemeral state
  //////////////////
  const showChartTooltip = useRecoilValue(showChartTooltipState);
  const setShowChartTooltip = useCallback(
    (v: boolean) => changeHiroChartSetting({ showChartTooltip: v }),
    [],
  );
  const [hiroSelectedSeriesList, setHiroSelectedSeriesList] = useState([]);
  const [startQueryDate, setStartQueryDate] =
    useRecoilState(startQueryDateState);
  const [endQueryDate, setEndQueryDate] = useRecoilState(endQueryDateState);
  const [lastSetDate, setLastSetDate] = useState(undefined);

  // HiroTradingViewContainer / useHiroData
  const [hiroData, setHiroData] = useState(InitialHiroData);
  const [sgData, setSgData] = useState(undefined);

  const [zoomTime, setZoomTime] = useState(undefined);

  const [bottomDrawerExpanded, setBottomDrawerExpanded] = useState(false);

  const standaloneMode = mode === 'standalone';

  const update = useCallback(
    (u: Partial<HiroContextValue>) => {
      for (const [k, v] of Object.entries(u)) {
        const update = () => onConfigChange({ [k]: v });
        switch (k) {
          // global
          case 'enableHiroV2':
            setEnableHiroV2(v);
            break;
          case 'enableHiroV3':
            break;
          case 'hiroTabs':
            setHiroTabs(v);
            break;
          case 'userDetails':
            break;
          case 'isInstitutionalLocal':
            setInstitutionalLocal(v);
            break;

          // per-inst
          case 'sym':
            standaloneMode ? setSym(v, ProductType.HIRO) : update();
            break;
          case 'includeHiroETH':
            standaloneMode ? setIncludeHiroETH(v) : update();
            break;
          case 'selectedLenses':
            standaloneMode ? setSelectedLenses(v) : update();
            break;
          case 'optionType':
            standaloneMode ? setOptionType(v) : update();
            break;
          case 'candleDuration':
            standaloneMode ? setCandleDuration(v) : update();
            break;
          case 'hiroUpdatingType':
            //standaloneMode ? setHiroUpdatingType(v) : update();
            break;
          case 'hiroLineType':
            standaloneMode ? setHiroLineType(v) : update();
            break;
          case 'priceLineType':
            standaloneMode ? setPriceLineType(v) : update();
            break;
          case 'showAlerts':
            standaloneMode ? setShowAlerts(v) : update();
            break;
          case 'hiroRollingWindowSeconds':
            standaloneMode ? setSumWindow(v) : update();
            break;
          case 'showHiroKeyLevels':
            standaloneMode ? setShowHiroKeyLevels(v) : update();
            break;
          case 'bottomCandlesType':
            standaloneMode ? setBottomCandlesType(v) : update();
            break;
          case 'hiroIndicators':
            standaloneMode ? setHiroIndicators(v) : update();
            break;
          case 'hiroTotalsEnabledPutsCallsDisabled':
            standaloneMode
              ? setHiroTotalsEnabledPutsCallsDisabled(v)
              : update();
            break;
          case 'hiroAllTradesEnabled':
            standaloneMode ? setHiroAllTradesEnabled(v) : update();
            break;
          case 'hiroNextExpiryEnabled':
            standaloneMode ? setHiroNextExpiryEnabled(v) : update();
            break;
          case 'hiroEnableCandles':
            standaloneMode ? setHiroEnableCandles(v) : update();
            break;
          case 'negativeTrendColor':
            //standaloneMode ? setNegativeTrendColor(v) : update();
            break;
          case 'positiveTrendColor':
            //standaloneMode ? setPositiveTrendColor(v) : update();
            break;

          // ephemeral
          case 'showChartTooltip':
            setShowChartTooltip(v);
            break;
          case 'hiroSelectedSeriesList':
            setHiroSelectedSeriesList(v);
            break;
          case 'endQueryDate':
            setEndQueryDate(v);
            break;
          case 'startQueryDate':
            setStartQueryDate(v);
            break;
          case 'lastSetDate':
            setLastSetDate(v);
            break;
          case 'hiroData':
            setHiroData(v);
            break;
          case 'sgData':
            setSgData(v);
            break;
          case 'zoomTime':
            setZoomTime(v);
            break;
          case 'bottomDrawerExpanded':
            setBottomDrawerExpanded(v);
            break;
          default:
            throw Error(`Unknown update field '${k}'`);
        }
      }
    },
    [
      standaloneMode,
      setEnableHiroV2,
      setHiroTabs,
      setInstitutionalLocal,
      setIncludeHiroETH,
      setSelectedLenses,
      setOptionType,
      setCandleDuration,
      setHiroLineType,
      setPriceLineType,
      setShowAlerts,
      setSumWindow,
      setShowHiroKeyLevels,
      setBottomCandlesType,
      setHiroIndicators,
      setHiroTotalsEnabledPutsCallsDisabled,
      setHiroAllTradesEnabled,
      setHiroNextExpiryEnabled,
      setHiroEnableCandles,
      setShowChartTooltip,
      setHiroSelectedSeriesList,
      setLastSetDate,
      setHiroData,
      setSgData,
      setZoomTime,
      setBottomDrawerExpanded,
      setStartQueryDate,
      setEndQueryDate,
    ],
  );

  return (
    <HiroContext.Provider
      value={{
        // global settings
        enableHiroV2,
        enableHiroV3,
        hiroTabs,
        userDetails,
        isInstitutionalLocal,

        // per inst settings
        sym: config?.sym ?? sym,
        includeHiroETH: config?.includeHiroETH ?? includeHiroETH,
        selectedLenses: config?.selectedLenses ?? selectedLenses,
        optionType: config?.optionType ?? optionType,
        candleDuration: config?.candleDuration ?? candleDuration,
        hiroUpdatingType: config?.hiroUpdatingType ?? hiroUpdatingType,
        hiroLineType: config?.hiroLineType ?? hiroLineType,
        priceLineType: config?.priceLineType ?? priceLineType,
        showAlerts: config?.showAlerts ?? showAlerts,
        showHiroKeyLevels: config?.showHiroKeyLevels ?? showHiroKeyLevels,
        bottomCandlesType: config?.bottomCandlesType ?? bottomCandlesType,
        hiroIndicators: config?.hiroIndicators ?? hiroIndicators,
        hiroTotalsEnabledPutsCallsDisabled:
          config?.hiroTotalsEnabledPutsCallsDisabled ??
          hiroTotalsEnabledPutsCallsDisabled,
        hiroAllTradesEnabled:
          config?.hiroAllTradesEnabled ?? hiroAllTradesEnabled,
        hiroNextExpiryEnabled:
          config?.hiroNextExpiryEnabled ?? hiroNextExpiryEnabled,
        hiroRollingWindowSeconds: config?.hiroRollingWindowSeconds ?? sumWindow,
        hiroEnableCandles: config?.hiroEnableCandles ?? hiroEnableCandles,
        negativeTrendColor: config?.negativeTrendColor ?? negativeTrendColor,
        positiveTrendColor: config?.positiveTrendColor ?? positiveTrendColor,

        // ephemeral settings
        showChartTooltip,
        hiroSelectedSeriesList,
        endQueryDate,
        startQueryDate,
        lastSetDate,
        hiroData,
        sgData,
        zoomTime,
        bottomDrawerExpanded,

        update,
        mode,
        canvasId,
        expandableContentButtonType,
      }}
    >
      {children}
    </HiroContext.Provider>
  );
};
